import { Add, Block, Close, Delete, Download, Edit, MoreVert } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Dialog, DialogContent, DialogTitle, Grid, IconButton, Menu, MenuItem, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AxiosError } from "axios";
import { format } from "date-fns";
import { enGB } from 'date-fns/locale/en-GB';
import { it } from 'date-fns/locale/it';
import { sl } from 'date-fns/locale/sl';
import I18n from 'i18next';
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomDialog } from "../../../../../common/components/CustomDialog";
import CustomLoader from "../../../../../common/components/CustomLoader";
import CustomLoadMore from "../../../../../common/components/CustomLoadMore";
import CustomSnackbar from "../../../../../common/components/CustomSnackbar";
import { getForm } from "../../../../../common/config/forms";
import { useAuth } from "../../../../../common/hooks/useAuth";
import { useLoading } from "../../../../../common/hooks/useLoading";
import { useReload } from "../../../../../common/hooks/useReload";
import { IBeehive } from "../../../../../common/interfaces/beehive";
import { IBeehiveStats, IExportStat } from "../../../../../common/interfaces/beehiveStats";
import { ICustomDialogProps } from "../../../../../common/interfaces/dialog";
import { ACTIONS } from "../../../../../common/interfaces/table";
import beehiveStatsService from "../../../../../common/services/beehivesStatsService";
import DialogStat from "./DialogStat";

interface IStatsTabProps {
    beehiveId: string | undefined;
    beehiveData: IBeehive | undefined;
    parentDialogOpen: boolean;
}

const itemsPerPage = 6;

const StatsTab: FC<IStatsTabProps> = (props) => {
    const { beehiveId, beehiveData, parentDialogOpen } = props;

    // global
    const { loginData, permissionsData, permissionsState } = useAuth();
    const { loading } = useLoading();
    const { reload, setReload } = useReload();
    const { t } = useTranslation();
    const locale = I18n.language;
    const theme = useTheme();
    const largeScreen = useMediaQuery(theme.breakpoints.up("lg"));
    const largeMediumScreen = useMediaQuery(theme.breakpoints.up("md"));
    const smallScreen = useMediaQuery(theme.breakpoints.up("sm"))

    const [action, setAction] = useState<ACTIONS>();
    const [anchorElActions, setAnchorElActions] = useState<(null | HTMLElement)[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currentBeehiveStats, setCurrentBeehiveStats] = useState<(IBeehiveStats & { actions: { type: ACTIONS, message: string }[] })[]>([]);
    const [currentBeehiveStat, setCurrentBeehiveStat] = useState<IBeehiveStats | undefined>(undefined);
    const [beehiveStatBody, setBeehiveStatBody] = useState<Partial<IBeehiveStats> | undefined>(undefined);
    const [beehiveCardData, setBeehiveCardData] = useState<{ [x: string]: string | number }[]>([]);

    // forms
    const exportStatForm = getForm<IExportStat>("exportStat");
    const exportDataForm = getForm<IExportStat>("exportData");

    // load more controls
    const [count, setCount] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    // dialogs
    const [openDialog, setOpenDialog] = useState(false);
    const [openExportDialog, setOpenExportDialog] = useState(false);
    const [openExportDataDialog, setOpenExportDataDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogBody, setDialogBody] = useState<React.ReactNode>(<></>);

    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [messageSnackbar, setMessageSnackbar] = useState<string>("");
    const [responseType, setResponseType] = useState<"success" | "error">("success");

    const getCardData = (stats: IBeehiveStats[]) => {
        let cardData: any[] = [];
        stats.forEach(stat => {
            let newStat = {}
            Object.keys(stat)
                .filter(key => JSON.stringify((stat as { [x: string]: any })[key]) !== '{}' && key !== "houseId" && key !== "id" && key !== "source")
                .forEach((key) => {
                    typeof (stat as { [x: string]: any })[key] === "object" ?
                        Object.keys((stat as { [x: string]: any })[key]).forEach((k) => {
                            if ((key === "mainFlower" && k === "flower" && (stat as { [x: string]: any })[key][k] === 'other')) {
                                return;
                            }
                            if (!k.includes("date")) {
                                newStat = {
                                    ...newStat,
                                    [
                                        (key === "temperature" || key === "humidity") ? (k + key.slice(0, 1).toUpperCase() + key.slice(1)) :
                                            (
                                                k === "type" ||
                                                key === "startingSpringDeposition" ||
                                                (key === "mainFlower" && (k === "type" || k === "isFlowering" || k === "flower")) ||
                                                (key === "varroaTreatment" && k === "type") ||
                                                (key === "stationaryInterference" && (k === "type" || k === 'referenceNumber'))
                                            ) ? (key + k.slice(0, 1).toUpperCase() + k.slice(1)) :
                                                (key === "otherSanitaryProblem" && k === "otherValue") ||
                                                    (key === "varroaTreatment" && k === "otherValue") ||
                                                    (key === "stationaryInterference" && k === "otherEventsValue") ?
                                                    (key + "Type") :
                                                    (key === "mainFlower" && k === "otherValue") ?
                                                        (key + "Flower") :
                                                        k
                                    ]: k === "timestamp" || k.includes("date") ?
                                            new Date((stat as { [x: string]: any })[key][k]).toLocaleDateString("en-EN", { hour: "numeric", minute: "2-digit", second: "2-digit" }) :
                                            (k === "otherValue" || k === "otherEventsValue") ?
                                                (stat as { [x: string]: any })[key][k] !== '' ?
                                                    (stat as { [x: string]: any })[key][k] :
                                                    t("beehiveDetailsPage.tabs.statsForm." + (stat as { [x: string]: any })[key]['type'])
                                                :
                                                k === 'referenceNumber' ?
                                                    (stat as { [x: string]: any })[key][k] !== '' ?
                                                        (stat as { [x: string]: any })[key][k] : ' - '
                                                    :
                                                    t("beehiveDetailsPage.tabs.statsForm." + (stat as { [x: string]: any })[key][k]) + (
                                                        key === "startingSpringDeposition" ? ' - ' + new Date((stat as { [x: string]: any })[key]['date']).toLocaleDateString(locale) : ''
                                                    )
                                }
                            }
                        })
                        :
                        newStat = {
                            ...newStat,
                            [key]: key === "timestamp" || key.includes("date") ?
                                new Date((stat as { [x: string]: any })[key]).toLocaleDateString("en-EN", { hour: "numeric", minute: "2-digit", second: "2-digit" }) :
                                t("beehiveDetailsPage.tabs.statsForm." + (stat as { [x: string]: any })[key])
                        }
                })
            cardData.push(newStat)
        })
        setBeehiveCardData(cardData)
    }

    const setActionsWithPermissions = () => {
        let tempActions: ({ type: ACTIONS, message: string })[] = []
        if (permissionsState && permissionsState.existEditStatsPermission)
            tempActions.push({
                type: ACTIONS.EDIT,
                message: "beehiveDetailsPage.tabs.editStatCardOption"
            })
        if (permissionsState && permissionsState.existDeleteStatsPermission)
            tempActions.push({
                type: ACTIONS.DELETE,
                message: "beehiveDetailsPage.tabs.deleteStatCardOption"
            })
        tempActions = tempActions.filter(action => action !== undefined)
        return tempActions
    }


    const getCurrentBeehiveStats = async (
        id: string,
        total: number,
        count?: number,
    ) => {
        try {
            const response = count ?
                await beehiveStatsService.getAll(id, undefined, (count - 1) * itemsPerPage, itemsPerPage) :
                await beehiveStatsService.getAll(id, undefined, total)

            setTotalItems(response.pageInfo?.totalItems)
            let rows = response.content.map(stat => {
                return {
                    ...stat,
                    actions: setActionsWithPermissions()
                }
            })
            setCurrentBeehiveStats(rows);
            getCardData(response.content)
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
        finally {
            setIsLoading(false);
            setReload(false);
        }
    }

    useEffect(() => {
        setIsLoading(true);
        if (beehiveId) {
            getCurrentBeehiveStats(beehiveId, totalItems, count)
        }
    }, [beehiveId, count])

    useEffect(() => {
        if (reload) {
            setIsLoading(true);
            if (beehiveId) {
                getCurrentBeehiveStats(beehiveId, totalItems, count)
            }
        }
    }, [reload])

    // action handler (icon buttons from the table)
    const onActionClicked = (label: ACTIONS, data: (IBeehiveStats & { actions: { type: ACTIONS, message: string }[] }) | undefined) => {
        setAction(label);
        if (data) {
            const { actions, ...statData } = data
            setCurrentBeehiveStat(statData);
        }
        switch (label) {
            case "add": {
                setDialogTitle(t("beehiveDetailsPage.tabs.addStatButtonLabel"));
                setDialogBody(undefined);
                setOpenDialog(true);
                break;
            }
            case "edit": {
                if (data)
                    setDialogTitle(t("beehiveDetailsPage.tabs.editStatButtonLabel"));
                setDialogBody(undefined);
                setOpenDialog(true);
                break;
            }
            case "delete": {
                setDialogTitle(t("beehiveDetailsPage.tabs.deleteStatButtonLabel"));
                setDialogBody(
                    <Typography variant="body1">
                        {t("beehiveDetailsPage.tabs.deleteStatDialogWarningText")}
                    </Typography>
                );
                setOpenDialog(true);
                break;
            }
            default: {
                break;
            }
        };
    }

    const onExportClicked = (data: IExportStat | undefined, source: "manual" | "import") => {
        if (beehiveId && data)
            beehiveStatsService.downloadCSV(beehiveId, source, data);
    }

    // close dialog handler
    const handleClose = () => {
        setCurrentBeehiveStat(undefined);
        setOpenDialog(false);
    };

    // api request handler
    const apiRequest = (data: Partial<IBeehiveStats> | undefined) => {
        if (beehiveId) {
            if (action === "add" && data)
                return beehiveStatsService.post(beehiveId, data)
            else if (action === "edit" && currentBeehiveStat && data)
                return beehiveStatsService.put(beehiveId, currentBeehiveStat.id, data)
            else if (action === "delete" && currentBeehiveStat)
                return beehiveStatsService.delete(beehiveId, currentBeehiveStat.id)
        } else {
            return Promise.reject()
        }
    }

    // confirm dialog handler
    const handleActionConfirm = async (data: Partial<IBeehiveStats> | undefined) => {
        handleClose();
        if (beehiveId) {
            try {
                await apiRequest(data);
                setOpenSnackbar(true);
                setResponseType("success");
                setMessageSnackbar(t("genericSuccess"));
                getCurrentBeehiveStats(beehiveId, totalItems, count);
            } catch (error: unknown) {
                setOpenSnackbar(true);
                setResponseType("error");
                if (error instanceof AxiosError) {
                    setMessageSnackbar(error.response?.data.content.message)
                } else {
                    setMessageSnackbar(t("genericError"));
                }
            }
        }
    }

    const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>, i: number) => {
        const elements = [...anchorElActions]
        elements.splice(i, 1, event.currentTarget)
        setAnchorElActions(elements);
    };

    const handleCloseActionsMenu = (i: number) => {
        const elements = [...anchorElActions]
        elements.splice(i, 1, null)
        setAnchorElActions(elements);
    };

    // dialog props
    const dialogExportStatProps: ICustomDialogProps = {
        type: "form",
        close: () => setOpenExportDialog(false),
        confirm: () => onActionClicked(ACTIONS.EXPORT, undefined),
        title: t("beehiveDetailsPage.exportDialogTitle"),
        open: openExportDialog,
        confirmButtonLabel: t("beehiveDetailsPage.exportButtonLabel"),
        closeButtonLabel: t("beehiveDetailsPage.tabs.statsForm.cancelButton"),
        fullWidth: true,
        formProps: {
            ...exportStatForm,
            initialValues: {
                from: null,
                to: null,
                startingSpringDeposition: true,
                colonyStrength: true,
                broodQuality: true,
                stockQuality: true,
                eatingType: true,
                looting: true,
                orphanage: true,
                varroaPresence: true,
                varroaTreatment: true,
                varroaDeathAfterTreatment: true,
                stationaryInterference: true,
                otherSanitaryProblem: true,
                mainFlower: true,
                swarming: true,
            },
            isDialog: true,
            submit: (data) => onExportClicked(data, "manual"),
            customStyles: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center"
            }
        }
    };

    // dialog props
    const dialogExportDataProps: ICustomDialogProps = {
        type: "form",
        close: () => setOpenExportDataDialog(false),
        confirm: () => onActionClicked(ACTIONS.EXPORT_DATA, undefined),
        title: t("beehiveDetailsPage.exportDataDialogTitle"),
        open: openExportDataDialog,
        confirmButtonLabel: t("beehiveDetailsPage.exportButtonLabel"),
        closeButtonLabel: t("beehiveDetailsPage.tabs.statsForm.cancelButton"),
        fullWidth: true,
        formProps: {
            ...exportDataForm,
            initialValues: {
                from: null,
                to: null,
                temperature: true,
                wet: true,
                weight: true,
                flightsNumber: true,
                coordinates: true,
            },
            isDialog: true,
            submit: (data) => onExportClicked(data, "import"),
            customStyles: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center"
            }
        }
    };

    const getDownloadPermissions = () => {
        return permissionsState &&
            permissionsState.existDownloadBeehivesPermission &&
            permissionsData &&
            (
                permissionsData['bh'].includes('bh_download_all') || (
                    permissionsData['bh'].includes("bh_download_only_area") &&
                    loginData &&
                    beehiveData &&
                    loginData.content.user.managedAreas.find(area => area.id === beehiveData.owner.areaId)
                ) || (
                    permissionsData['bh'].includes('bh_download_only_owned') &&
                    loginData &&
                    beehiveData &&
                    beehiveData.ownerId === loginData.content.user.id
                ))
    }

    const getCreatePermissions = () => {
        return permissionsState &&
            permissionsState.existCreateStatsPermission &&
            permissionsData &&
            (
                permissionsData['bhstats'].includes('bhstats_create_all') || (
                    permissionsData['bhstats'].includes("bhstats_create_only_area") &&
                    loginData &&
                    beehiveData &&
                    loginData.content.user.managedAreas.find(area => area.id === beehiveData.owner.areaId)
                ) || (
                    permissionsData['bhstats'].includes('bhstats_create_only_owned') &&
                    loginData &&
                    beehiveData &&
                    beehiveData.ownerId === loginData.content.user.id
                ))
    }

    const renderAddStatDialog = () => {
        return <Dialog open={openDialog} fullWidth scroll="paper">
            <DialogTitle sx={{ fontWeight: 600 }}>
                {t(dialogTitle)}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <Close />
            </IconButton>
            <DialogContent sx={{ py: 0, px: 3 }}>
                {action === "delete" ? dialogBody : <DialogStat
                    currentBeehiveStat={currentBeehiveStat}
                    setBeehiveStatBody={setBeehiveStatBody}
                />}
            </DialogContent>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 2,
                    px: 3,
                }}
            >
                <Button
                    aria-label="close"
                    variant="outlined"
                    onClick={() => { handleClose(); }}
                >
                    {t("beehiveDetailsPage.tabs.statsForm.cancelButton")}
                </Button>
                <Button
                    onClick={() => handleActionConfirm(beehiveStatBody)}
                    variant="contained"
                >
                    {action === "delete" ? t("beehiveDetailsPage.tabs.statsForm.confirmButton") : t("beehiveDetailsPage.tabs.statsForm.saveChangesButton")}
                </Button>
            </Box>
        </Dialog>
    }

    return <Box sx={{ py: 2 }}>
        <CustomDialog {...dialogExportStatProps}>{dialogBody}</CustomDialog>
        <CustomDialog {...dialogExportDataProps}>{dialogBody}</CustomDialog>
        {renderAddStatDialog()}
        <CustomSnackbar
            type={responseType}
            message={messageSnackbar}
            open={openSnackbar}
            onClose={() => setOpenSnackbar(false)}
        />
        {
            loading && !openDialog && !parentDialogOpen ? <CustomLoader /> :
                <Box>
                    <Box sx={{ display: "flex", flexDirection: largeScreen ? "row" : "column", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: "flex", width: largeScreen ? "auto" : "100%" }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>{t("beehiveDetailsPage.tabs.statsTabTitle")}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: smallScreen ? "row" : "column", justifyContent: "flex-end", mb: 2 }}>
                            {
                                getCreatePermissions() &&
                                <Button
                                    variant="outlined"
                                    sx={{
                                        mr: smallScreen ? 2 : 0,
                                        mb: smallScreen ? 0 : 1
                                    }}
                                    onClick={() => onActionClicked(ACTIONS.ADD, undefined)}
                                >
                                    <Add sx={{ mr: !smallScreen ? 0 : 0.5 }} />
                                    {t("beehiveDetailsPage.tabs.addStatButtonLabel")}
                                </Button>
                            }
                            {
                                getDownloadPermissions() &&
                                <Button variant="outlined" color="secondary"
                                    sx={{
                                        mr: smallScreen ? 2 : 0,
                                        mb: smallScreen ? 0 : 1
                                    }}
                                    onClick={() => setOpenExportDialog(true)}
                                >
                                    <Download sx={{ mr: !smallScreen ? 0 : 0.5 }} />
                                    {t("beehiveDetailsPage.tabs.exportStatsButtonLabel")}
                                </Button>
                            }
                            {
                                getDownloadPermissions() &&
                                <Button variant="outlined" color="secondary"
                                    sx={{
                                        mr: smallScreen ? 2 : 0,
                                        mb: smallScreen ? 0 : 1
                                    }}
                                    onClick={() => setOpenExportDataDialog(true)}
                                >
                                    <Download sx={{ mr: !smallScreen ? 0 : 0.5 }} />
                                    {t("beehiveDetailsPage.tabs.exportDataButtonLabel")}
                                </Button>
                            }
                        </Box>
                    </Box>
                    <Grid container rowSpacing={2} columnSpacing={2}>
                        {
                            beehiveCardData.map(
                                (stat, i) =>
                                    <Grid key={stat.timestamp + "-" + i} item xs={12}>
                                        <Card sx={{ position: "relative" }}>
                                            <CardContent sx={{ py: 1.5, display: "flex", flexWrap: "wrap" }}>
                                                <Box sx={{
                                                    width: "100%",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    borderBottom: 1,
                                                    mb: 1
                                                }}>
                                                    <Typography gutterBottom variant="h6" component="div">
                                                        {t("beehiveDetailsPage.tabs.detectedStatCardTitle")} - {
                                                            format(
                                                                stat.timestamp,
                                                                "PP",
                                                                { locale: locale === "it-IT" ? it : locale === "sl-SI" ? sl : enGB }
                                                            )
                                                        }
                                                    </Typography>
                                                    <IconButton
                                                        aria-label="actions"
                                                        aria-controls="menu-actions"
                                                        aria-haspopup="true"
                                                        onTouchStart={(event) => event.stopPropagation()}
                                                        onMouseDown={(event) => event.stopPropagation()}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            handleOpenActionsMenu(event, i)
                                                        }}
                                                        sx={{
                                                            position: "absolute",
                                                            top: 8,
                                                            right: 8,
                                                            background: "white"
                                                        }}
                                                        color="primary"
                                                    >
                                                        <MoreVert />
                                                    </IconButton>
                                                    <Menu
                                                        sx={{ mt: '45px' }}
                                                        id="menu-appbar"
                                                        anchorEl={anchorElActions[i]}
                                                        anchorOrigin={{
                                                            vertical: 'top',
                                                            horizontal: 'right',
                                                        }}
                                                        transformOrigin={{
                                                            vertical: 'top',
                                                            horizontal: 'right',
                                                        }}
                                                        open={Boolean(anchorElActions[i])}
                                                        onClose={() => handleCloseActionsMenu(i)}
                                                        onClick={(event) => event.stopPropagation()}
                                                    >
                                                        {currentBeehiveStats[i].actions.map((action) => (
                                                            <MenuItem
                                                                key={action.type}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    onActionClicked(action.type, currentBeehiveStats[i]);
                                                                    handleCloseActionsMenu(i);
                                                                }}
                                                            >
                                                                <Typography
                                                                    color={action.type === "info" ? "secondary" : action.type === "delete" ? "error" : "primary"}
                                                                    sx={{ display: "flex", alignItems: "center", mb: 0 }}
                                                                >
                                                                    {action.type === "edit" && <Edit sx={{ mr: 0.5 }} />}
                                                                    {action.type === "delete" && <Delete sx={{ mr: 0.5 }} />}
                                                                    {t(action.message)}
                                                                </Typography>
                                                            </MenuItem>
                                                        ))}
                                                    </Menu>
                                                </Box>
                                                {
                                                    Object.keys(stat).filter(key => key !== "timestamp").map((key, index) =>
                                                        <Typography gutterBottom variant="body1" component="div" color="text.secondary" sx={{
                                                            width: largeMediumScreen ? "33.3%" : smallScreen ? "50%" : "100%",
                                                            display: "flex",
                                                            mb: 0,
                                                            p: 1
                                                        }} key={stat.timestamp + "-" + key + "-" + index}>
                                                            <Box sx={{
                                                                display: "flex",
                                                                flexWrap: "wrap",
                                                                flexDirection: smallScreen ? "row" : "column",
                                                                width: "100%"
                                                            }}>
                                                                <Typography
                                                                    component="div"
                                                                    variant="body1"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        mr: .5,
                                                                        width: "100%",
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                    }}>
                                                                    {t("beehiveDetailsPage.tabs." + key)}:
                                                                </Typography> {
                                                                    (stat as { [x: string]: any })[key]
                                                                }
                                                            </Box>

                                                        </Typography>
                                                    )
                                                }
                                            </CardContent>
                                        </Card>
                                    </Grid>
                            )
                        }

                    </Grid>
                    {
                        beehiveCardData.length === 0 &&
                        <Typography sx={{ mt: 2, fontWeight: 600, display: 'flex', alignItems: "center" }}>
                            <Block sx={{ mr: 1 }} />
                            {t("beehivesPage.noDataLabel")}
                        </Typography>
                    }
                    {
                        Math.round(totalItems / itemsPerPage) >= count &&
                        totalItems > itemsPerPage &&
                        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                            <CustomLoadMore count={count} setCount={setCount} loading={isLoading} />
                        </Box>
                    }
                </Box>
        }
    </Box >
}

export default StatsTab;